'use client'

/**
 * "Next steps" under a Home answer (#16813, part of #16812).
 *
 * Every PARTIAL / ADJACENT / NOT_YET turn carries at least one step on its
 * presentation (`nextStepsForPlan`, `@/lib/ai-chat/answer-class-shapes`), and
 * this row is where they render — so a missing answer still moves the work
 * forward instead of ending at "not connected yet".
 *
 *  - A **pivot** is a question the agent can answer now. Pressing it asks
 *    that question through the composer path (`onAskPivot`), exactly as if
 *    it had been typed.
 *  - An **entity** step (#16822) — "Which Michael Chen do you mean?" —
 *    renders its people as one-click choices above the row
 *    (`./entity-choices`); choosing asks the question again about them.
 *  - **Connect now** (#16818) is a link to the provider's connection page,
 *    carrying the way back to this conversation. Following it reports
 *    `ai_chat.connect_now_clicked` (domain and provider, never the href) and
 *    approves the step as `connect`; the connect itself happens on that page,
 *    behind its own admin gate.
 *  - Any other step opens its {@link DraftCard}: edit, then Approve or Not
 *    now. One card at a time; pressing the open step again closes it, and
 *    closing returns focus to the step that opened it.
 *
 *  - A **scope** step (#16823) is a question asked INSTEAD of an answer. It
 *    renders as {@link ScopeQuestion} rather than as a chip, and a pick goes
 *    through `onAnswerScope` so the turn carries it.
 *
 * Each step reports `ai_chat.next_step_shown` once when the row mounts —
 * its kind, its type and the turn's class, never the question — and a pivot
 * reports `ai_chat.pivot_asked`. Fire-and-forget: lost telemetry is never an
 * error on screen.
 *
 * The heading says "Next steps". The internal name for a hand-off step never
 * reaches the screen.
 */

import { type MouseEvent, useEffect, useId, useRef, useState } from 'react'

import { approveNextStep, trackNextStepEvent } from '@/app/actions/next-steps'
import { ArrowRight, Bell, PenLine, Plug } from '@/components/icons'
import { Chip, ChipLink } from '@/components/ui/chip'
import type { EntityStep, NextStep } from '@/lib/ai-chat/next-steps'
import type { PresentationClass } from '@/lib/ai-chat/presentation-class'
import type { EntityPin } from '@/lib/ai-chat/resolve-entities'
import type { ScopeAnswer } from '@/lib/ai-chat/scope-question'

import { DraftCard } from './draft-card'
import { EntityChoices } from './entity-choices'
import { ScopeQuestion } from './scope-question'

function stepEventProperties(step: NextStep, answerClass: PresentationClass) {
  return step.kind === 'offramp'
    ? { kind: step.kind, type: step.type, answerClass }
    : { kind: step.kind, answerClass }
}

type ChipStep = Exclude<NextStep, EntityStep | { kind: 'scope' }>

type OffRampStep = Extract<NextStep, { kind: 'offramp' }>

/** A Connect now step the row renders as a link (#16818). */
function isConnectLink(step: NextStep): step is OffRampStep & { href: string } {
  return step.kind === 'offramp' && step.type === 'connect' && !!step.href
}

/**
 * How long Connect now waits for its two reports before leaving the page
 * (#16818). Long enough for both actions to land on a healthy connection;
 * short enough that a slow one never holds the asker on Home.
 */
export const CONNECT_REPORT_WAIT_MS = 400

/**
 * Connect now, followed (#16818). Both reports are lost-is-fine: the
 * navigation is the asker's outcome, and a lost event or an unrecorded take
 * must not hold it up. Settles when both have.
 */
function reportConnect(
  step: OffRampStep,
  answerClass: PresentationClass,
  unmetNeedId: string | null,
): Promise<unknown> {
  const reports: Promise<unknown>[] = [
    trackNextStepEvent('ai_chat.connect_now_clicked', {
      ...stepEventProperties(step, answerClass),
      ...(step.connect ?? {}),
    }),
  ]
  if (unmetNeedId !== null) {
    reports.push(approveNextStep({ unmetNeedId, type: 'connect' }))
  }
  return Promise.allSettled(reports)
}

/** A click the browser means for a new tab or window: leave it alone. */
export function opensElsewhere(event: MouseEvent): boolean {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
}

/**
 * Leave for the connection page at the DOCUMENT level, not as a soft
 * navigation: the two reports above are server actions, and one still in
 * flight when a soft navigation commits writes Home's router state back
 * over it (#13318, `src/components/meetings/meeting-navigation.ts`).
 * Waiting briefly first lets them land rather than be cut off by the unload.
 * Also the way a use-case recommendation's Start leaves (#16826).
 */
export async function leaveAfterReports(
  href: string,
  reported: Promise<unknown>,
) {
  await Promise.race([
    reported,
    new Promise(resolve => setTimeout(resolve, CONNECT_REPORT_WAIT_MS)),
  ])
  window.location.assign(href)
}

export function NextSteps({
  steps,
  answerClass,
  unmetNeedId,
  onAskPivot,
  onAnswerScope,
  answered = false,
  disabled = false,
}: {
  steps: readonly NextStep[]
  answerClass: PresentationClass
  /** The turn's unmet-need record, or `null` when none was written. */
  unmetNeedId: string | null
  /** Ask a question as the asker's next turn. */
  onAskPivot: (question: string, entityPick?: EntityPin) => void
  /**
   * #16823 — answer the scoping question as the next turn. Omitted, a pick
   * is asked as a plain question, carrying nothing.
   */
  onAnswerScope?: (question: string, answer: ScopeAnswer | null) => void
  /** #16823 — the reader already replied to this turn's scoping question. */
  answered?: boolean
  /** True while a turn is streaming: a pivot would race it. */
  disabled?: boolean
}) {
  const headingId = useId()
  const [openId, setOpenId] = useState<string | null>(null)
  const buttons = useRef(new Map<string, HTMLButtonElement>())

  // Once per set of steps, not per render: a StrictMode re-run or a
  // transcript re-render of the same turn is not a second showing.
  const reportedFor = useRef<string | null>(null)
  const signature = steps.map(step => step.id).join('|')
  useEffect(() => {
    if (reportedFor.current === signature) return
    reportedFor.current = signature
    for (const step of steps) {
      trackNextStepEvent(
        'ai_chat.next_step_shown',
        stepEventProperties(step, answerClass),
      ).catch(() => {
        // Telemetry only; nothing for the asker to act on.
      })
    }
  }, [signature, steps, answerClass])

  if (steps.length === 0) return null

  // A CLARIFY turn carries its question and nothing else (#16823).
  const scope = steps.find(step => step.kind === 'scope')
  if (scope?.kind === 'scope') {
    return (
      <ScopeQuestion
        step={scope}
        disabled={disabled || answered}
        onAnswer={(question, answer) => {
          if (onAnswerScope) onAnswerScope(question, answer)
          else onAskPivot(question)
        }}
      />
    )
  }
  const entitySteps = steps.filter(
    (step): step is EntityStep => step.kind === 'entity',
  )
  const chipSteps = steps.filter(
    (step): step is ChipStep => step.kind !== 'entity' && step.kind !== 'scope',
  )

  const open = steps.find(
    (step): step is OffRampStep =>
      step.kind === 'offramp' && step.id === openId && !isConnectLink(step),
  )

  function press(step: ChipStep) {
    if (step.kind === 'pivot') {
      trackNextStepEvent(
        'ai_chat.pivot_asked',
        stepEventProperties(step, answerClass),
      ).catch(() => {})
      onAskPivot(step.question)
      return
    }
    setOpenId(current => (current === step.id ? null : step.id))
  }

  function close(stepId: string) {
    setOpenId(null)
    buttons.current.get(stepId)?.focus()
  }

  return (
    <section
      data-testid="ai-chat-next-steps"
      aria-labelledby={chipSteps.length > 0 ? headingId : undefined}
      className="mt-3"
    >
      {entitySteps.map(step => (
        <EntityChoices
          key={step.id}
          step={step}
          onAsk={onAskPivot}
          disabled={disabled}
          className="mb-3"
        />
      ))}
      {chipSteps.length > 0 && (
        <h4
          id={headingId}
          className="text-xs font-semibold text-text-secondary"
        >
          Next steps
        </h4>
      )}
      <ul className="mt-1.5 flex flex-wrap gap-2 empty:hidden">
        {chipSteps.map(step => (
          <li key={step.id}>
            {isConnectLink(step) ? (
              <ChipLink
                variant="outline"
                href={step.href}
                onClick={event => {
                  const reported = reportConnect(step, answerClass, unmetNeedId)
                  if (opensElsewhere(event)) return
                  event.preventDefault()
                  void leaveAfterReports(step.href, reported)
                }}
              >
                <Plug className="h-3 w-3 shrink-0" aria-hidden />
                {step.label}
              </ChipLink>
            ) : (
              <Chip
                variant="outline"
                ref={node => {
                  if (node) buttons.current.set(step.id, node)
                  else buttons.current.delete(step.id)
                }}
                disabled={step.kind === 'pivot' && disabled}
                aria-expanded={
                  step.kind === 'offramp' ? openId === step.id : undefined
                }
                onClick={() => press(step)}
              >
                {step.kind === 'pivot' ? (
                  <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
                ) : step.type === 'monitor' ? (
                  // #16819 — a watch, not a draft: nothing to write.
                  <Bell className="h-3 w-3 shrink-0" aria-hidden />
                ) : (
                  <PenLine className="h-3 w-3 shrink-0" aria-hidden />
                )}
                {step.kind === 'pivot' ? step.question : step.label}
              </Chip>
            )}
          </li>
        ))}
      </ul>
      {open && (
        <DraftCard
          key={open.id}
          step={open}
          unmetNeedId={unmetNeedId}
          onClose={() => close(open.id)}
        />
      )}
    </section>
  )
}
